/**
 * ChatSidebar - Sidebar with new chat button and conversation history
 */

import { trackEvent } from '@/utils/analytics'
import { useState } from 'react'
import { Plus, MessageSquare, Loader2, PanelLeftClose, PanelLeftOpen, Trash2, X, Search } from 'lucide-react'
import clsx from 'clsx'
import type { Conversation } from '../../types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useTranslation } from '@/i18n'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface SearchStats {
  total: number
  matched: number
  deepSearched: number
}

// Helper to format relative time
function formatRelativeTime(
  date: Date,
  t: (key: string, opts: Record<string, unknown>) => string,
): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return t('chatv2:time.just_now', { defaultValue: 'Just now' })
  if (diffMins < 60) return t('chatv2:time.minutes_ago', { count: diffMins, defaultValue: '{{count}}m ago' })
  if (diffHours < 24) return t('chatv2:time.hours_ago', { count: diffHours, defaultValue: '{{count}}h ago' })
  if (diffDays < 7) return t('chatv2:time.days_ago', { count: diffDays, defaultValue: '{{count}}d ago' })
  return date.toLocaleDateString()
}

interface ChatSidebarProps {
  conversations: Conversation[]
  currentConversationId: string | null
  onNewChat: () => void
  onSelectConversation: (conversation: Conversation) => void
  onDeleteConversation: (conversationId: string) => Promise<void>
  onToggleSidebar?: () => void
  isCollapsed?: boolean
  isLoading?: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  searchStats: SearchStats
}

export function ChatSidebar({
  conversations,
  currentConversationId,
  onNewChat,
  onDeleteConversation,
  onSelectConversation,
  onToggleSidebar,
  isCollapsed = false,
  isLoading,
  searchQuery,
  onSearchChange,
  searchStats,
}: ChatSidebarProps) {
  const { t } = useTranslation()
  // No internal search state - controlled by parent

  // Collapsed state - show expand button, new chat icon, and conversation icons
  if (isCollapsed) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-dark-card border-r border-gray-200 dark:border-dark-border">
        {/* Expand Toggle Button */}
        {onToggleSidebar && (
          <div className="p-2 flex-shrink-0 border-b border-gray-200 dark:border-dark-border">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onToggleSidebar}
                    className="w-10 h-10 flex items-center justify-center text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
                  >
                    <PanelLeftOpen className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t('chatv2:sidebar.expand', { defaultValue: 'Expand sidebar' })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* New Chat Button (collapsed) */}
        <div className="p-2 flex-shrink-0">
          <NewChatButtonWithTooltip onNewChat={onNewChat} />
        </div>

        {/* Conversation Icons */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.map((conversation) => (
            <ConversationIconWithTooltip
              key={conversation.id}
              conversation={conversation}
              isActive={conversation.id === currentConversationId}
              onSelect={() => onSelectConversation(conversation)}
            />
          ))}
        </div>
      </div>
    )
  }

  // Expanded state - show full conversation list
  return (
    <div className="flex flex-col h-full bg-white dark:bg-dark-card border-r border-gray-200 dark:border-dark-border">
      {/* Header with Menu and collapse button */}
      {onToggleSidebar && (
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm font-medium text-gray-900 dark:text-dark-text">{t('chatv2:sidebar.menu', { defaultValue: 'Menu' })}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onToggleSidebar}
                  className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
                >
                  <PanelLeftClose className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('chatv2:sidebar.collapse', { defaultValue: 'Collapse sidebar' })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* New Chat Button */}
      <div className="px-4 py-3">
        <button
          onClick={() => {
            trackEvent('chat-conversation_created', { source: 'sidebar' })
            onNewChat()
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t('chatv2:sidebar.new_chat', { defaultValue: 'New Chat' })}
        </button>
      </div>

      {/* History Header */}
      <div className="px-4 pt-2 pb-2">
        <h2 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
          {t('chatv2:sidebar.history', { defaultValue: 'HISTORY' })}
        </h2>
        {/* Search Input */}
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            {isLoading ? (
              <Loader2 className="w-4 h-4 text-orange-500 animate-spin" style={{ animationDuration: '1s' }} />
            ) : (
              <Search className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <input
            type="text"
            placeholder={t('chatv2:sidebar.search_placeholder', { defaultValue: 'Search conversations...' })}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2 text-sm bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 dark:hover:bg-dark-accent rounded transition-colors"
              title={t('chatv2:sidebar.clear_search', { defaultValue: 'Clear search' })}
            >
              <X className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            </button>
          )}
        </div>
        {/* Search stats */}
        {searchQuery && searchStats.matched < searchStats.total && (
          <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1.5 pl-1">
            {t('chatv2:sidebar.search_stats', { count: searchStats.matched, total: searchStats.total, defaultValue: '{{count}} of {{total}} conversations' })}
            {searchStats.deepSearched > 0 &&
              t('chatv2:sidebar.search_stats_deep', { count: searchStats.deepSearched, defaultValue: ' • {{count}} searched deeply' })}
          </p>
        )}
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {isLoading ? (
          <div className="py-12 text-center">
            <Loader2 className="h-6 w-6 mx-auto text-gray-400 dark:text-gray-500 animate-spin mb-2" />
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">{t('chatv2:sidebar.loading', { defaultValue: 'Loading conversations...' })}</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="py-12 text-center">
            <MessageSquare className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              {searchQuery
                ? t('chatv2:sidebar.no_matching', { defaultValue: 'No matching conversations' })
                : t('chatv2:sidebar.no_conversations', { defaultValue: 'No conversations yet' })}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {searchQuery
                ? t('chatv2:sidebar.try_different', { defaultValue: 'Try a different search term' })
                : t('chatv2:sidebar.start_to_begin', { defaultValue: 'Start a new chat to begin' })}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isActive={conversation.id === currentConversationId}
                onSelect={() => onSelectConversation(conversation)}
                onDelete={() => onDeleteConversation(conversation.id)}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  )
}

// New Chat button with tooltip for collapsed mode
interface NewChatButtonWithTooltipProps {
  onNewChat: () => void
}

function NewChatButtonWithTooltip({ onNewChat }: NewChatButtonWithTooltipProps) {
  const { t } = useTranslation()
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => {
              trackEvent('chat-conversation_created', { source: 'sidebar' })
              onNewChat()
            }}
            className="w-10 h-10 flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {t('chatv2:sidebar.new_chat', { defaultValue: 'New Chat' })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Conversation item in expanded sidebar with delete functionality
interface ConversationItemProps {
  conversation: Conversation
  isActive: boolean
  onSelect: () => void
  onDelete: () => Promise<void>
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
}: ConversationItemProps) {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleConfirmDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await onDelete()
      trackEvent('chat-conversation_deleted', { conversation_id: conversation.id })
    } catch {
      // onDelete already shows error toast via useConversationsV2
    }
  }

  return (
    <div className="relative group">
      <button
        onClick={() => {
          if (!isActive) {
            trackEvent('chat-conversation_switched', { conversation_id: conversation.id })
          }
          onSelect()
        }}
        className={clsx(
          'w-full flex items-start gap-2 px-3 py-2 rounded-lg text-left transition-colors',
          isActive
            ? 'bg-gray-100 dark:bg-dark-accent text-gray-900 dark:text-dark-text'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted'
        )}
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate block">
            {conversation.firstMessage || t('chatv2:sidebar.new_conversation', { defaultValue: 'New conversation' })}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {formatRelativeTime(conversation.updatedAt, t)}
          </span>
        </div>

        {/* Delete button with AlertDialog */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-dark-border flex-shrink-0"
                title={t('chatv2:sidebar.delete_conversation', { defaultValue: 'Delete conversation' })}
              >
                <Trash2 className="h-3 w-3 text-gray-400 dark:text-gray-500 hover:text-red-500" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('chat:delete_dialog.title')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('chat:delete_dialog.description')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
                  {t('common:buttons.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmDelete}
                  className="bg-red-500 hover:bg-red-600 focus:ring-red-500"
                >
                  {t('common:buttons.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </button>
    </div>
  )
}

// Conversation icon with tooltip for collapsed mode
interface ConversationIconWithTooltipProps {
  conversation: Conversation
  isActive: boolean
  onSelect: () => void
}

function ConversationIconWithTooltip({
  conversation,
  isActive,
  onSelect,
}: ConversationIconWithTooltipProps) {
  const { t } = useTranslation()
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onSelect}
            className={clsx(
              'w-10 h-10 flex items-center justify-center rounded-lg transition-colors',
              isActive
                ? 'bg-gray-100 dark:bg-dark-accent text-gray-900 dark:text-dark-text'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted'
            )}
          >
            <MessageSquare className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {conversation.firstMessage || t('chatv2:sidebar.new_conversation', { defaultValue: 'New conversation' })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default ChatSidebar
